<script setup lang="ts">

import { ref, computed } from "vue";
import type { IMyGarageViewModel } from "../..";
import { useAppConfiguration } from "@domain/AppConfiguration";
import { useViewModelInstance as useCarbucksViewModel } from "@app/Carbucks";
import { Transition as Transitions } from "@app/Components/Transition";

const model = defineModel<IMyGarageViewModel>( { required: true } );  
const app = useAppConfiguration();
const hasLinkedCards = ref<boolean|null>( null );

// Dont wait for the carbucks view model, but when its ready set the linked cards state
// so we can drop in the CTA to link a card if needed
useCarbucksViewModel().then( carbucks => 
{
    hasLinkedCards.value = carbucks.profile.hasLinkedCards;
});

const vehicleDisplayName = computed( () => 
{
    if( model.value.vehicle.hasDisplayName ) return model.value.vehicle.displayName;
    const userEnteredName = `${ ( model.value.vehicle.make ?? '' ) } ${ ( model.value.vehicle.model ?? '' ) }`.trim();
    if( userEnteredName.length > 0 ) return userEnteredName;
    return undefined;
});

</script>

<template>

    <div class="px-5 3xl:px-7 w-full flex flex-col tracking-wider">
                           
        <div class="flex flex-col gap-y-2 2xl:gap-y-3 2xl:pb-2">
            
            <!-- UPDATE ODOMETER -->
            <Transitions.FadeScale>
                <div v-if="model.vehicle.isOdometerUpdateRequired"
                    class="text-base 2xl:text-xl flex items-center justify-start gap-2 2xl:gap-4 mr-auto">
                    <RouterLink to="/my-garage/set-odometer"
                                class="flex items-center justify-center
                                       p-3 -ml-1 mb-0 rounded-lg shadow
                                       bg-white/10
                                       text-secondary-500 brightness-[140%] hover:brightness-[155%]">
                        <Icon name="odometer" class="text-xl md:text-2xl 2xl:text-4xl"/>
                    </RouterLink>            
                    <span>
                        You Need To Set Your Odometer
                    </span>
                </div>
            </Transitions.FadeScale>

            <!-- LINK CREDIT CARD / ONLY APPEARS IF THE HOME DEALERSHIP IS SET
                 AND DISAPPEARS ONCE THE USER LINKS A CARD -->
            <Transitions.FadeScale>
                <div v-if="app.isGeneric == false && hasLinkedCards === false"
                     class="text-base 2xl:text-xl flex items-center justify-start gap-2 2xl:gap-4 mr-auto">
                    <RouterLink to="/carbucks"
                                class="flex items-center justify-center
                                       p-3 -ml-1 mb-0 rounded-lg shadow
                                       bg-white/10
                                       text-secondary-500 brightness-[140%] hover:brightness-[155%]">
                        <Icon name="credit-card" class="text-xl md:text-2xl 2xl:text-4xl"/>
                    </RouterLink>            
                    <span>
                        Link A Card To Earn Rewards
                    </span>
                </div>
            </Transitions.FadeScale>
            
            <!-- VEHICLE DETAILS / EDIT PROPERTIES BUTTON -->
            <div class="text-base 2xl:text-xl flex items-center justify-start gap-2 2xl:gap-4 mr-auto">
                <RouterLink to="/my-garage/vehicle-details"
                            class="flex items-center justify-center
                                   p-3 -ml-1 mb-0 rounded-lg shadow hover:brightness-125
                                   bg-white/10
                                   text-white/80"
                            :class="{
                                '!bg-gradient-to-b !from-secondary-750/50 !via-secondary-800/50 !to-secondary-700/50 !brightness-[140%] hover:!brightness-[155%]': vehicleDisplayName == undefined,
                                'pointer-events-none cursor-default': model.vehicle.isShared
                            }">
                    <Icon name="car" class="text-xl md:text-2xl 2xl:text-4xl"/>
                </RouterLink>            
                <span v-if="vehicleDisplayName" class="opacity-60">
                    {{ vehicleDisplayName }}
                </span>
                <span v-else>
                    Update Your Vehicle Details
                </span>
            </div>
                        
            <!-- HOME DEALERSHIP / OPEN & SET DEALERSHIP BUTTON -->
            <div class="text-base 3xl:text-xl flex items-center justify-start gap-2 2xl:gap-4 mr-auto">
                <RouterLink to="/my-garage/dealership"
                            class="flex items-center justify-center
                                   p-3 -ml-1 mb-0 rounded-lg shadow hover:brightness-125
                                   bg-white/10
                                   text-white"
                            :class="{
                                '!bg-secondary-600 hover:!brightness-[110%]': app.isGeneric 
                            }">
                    <Icon name="home" class="text-xl md:text-2xl 2xl:text-4xl"/>
                </RouterLink>            
                <span v-if="app.isGeneric == false" class="opacity-60">
                    {{ app.name }}
                </span>
                <span v-else>
                    Set Your Home Dealership
                </span>
            </div>

            <!-- CONCIERGE MESSAGE - ONLY SHOWS WHEN THERE IS A MESSAGE TO DISPLAY -->
            <div v-if="model.carePlan.carbucksRewards.isConciergeMessagesEnabled && model.carePlan.carbucksRewards.hasMessages"
                 class="text-base 3xl:text-lg flex items-center justify-start gap-2 mr-auto">
                <RouterLink to="/my-garage/concierge"
                            class="flex items-center justify-center
                                   p-3 -ml-1 mb-0 rounded-lg shadow
                                   bg-white/10
                                   text-secondary-500 brightness-[140%] hover:brightness-[155%]">
                    <Icon name="special:concierge" class="text-xl md:text-2xl 2xl:text-4xl opacity-50"/>

                    <div class="absolute -top-1.5 -right-1.5
                                flex items-center justify-center aspect-square w-[1.25rem] rounded-full 
                                text-xs text-white ring-1 ring-gray-900/20
                                bg-[#ff0055] shadow-2-strong">
                        {{ model.carePlan.carbucksRewards.messages.length }}
                    </div>
                </RouterLink>            
                <span>
                    You have {{ model.carePlan.carbucksRewards.messages.length }} new Concierge message{{ model.carePlan.carbucksRewards.messages.length == 1 ? '' : 's' }}
                </span>
            </div>

        </div>
    
    </div>

</template>